import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { extractUsername } from "@/utils/extractUsername";
import {
  Loader2,
  Mail,
  MessageCircle,
  Phone,
  User,
  UserMinus,
  UserPlus,
  Video,
  X,
  CheckCircle,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { getUserProfile } from "../api/friend.api";
import { Friend } from "../types/friend.type";

interface UserProfileCardProps {
  user: Friend;
  onMessage?: (userId: number) => void;
  onCall?: (userId: number) => void;
  onVideoCall?: (userId: number) => void;
  onUnfriend?: (userId: number) => void;
  onAddFriend?: (userId: number) => void;
  onAcceptRequest?: (requestId: number) => void;
  onDeclineRequest?: (requestId: number) => void;
  onCancelRequest?: (requestId: number) => void;
  type?: "received" | "sent";
}

const UserProfileCard: React.FC<UserProfileCardProps> = ({
  user,
  onMessage,
  onCall,
  onVideoCall,
  onUnfriend,
  onAddFriend,
  onAcceptRequest,
  onDeclineRequest,
  onCancelRequest,
  type,
}) => {
  const [userProfile, setUserProfile] = useState<Friend | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  const fetchUserProfile = async (id: number) => {
    try {
      setIsLoadingProfile(true);
      setProfileError(null);
      const res = await getUserProfile(id);
      setUserProfile(res.data);
    } catch (error) {
      console.error("Error fetching user profile:", error);
      setProfileError("Không thể tải thông tin chi tiết");
      // Fallback to basic user data
      setUserProfile(user);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  useEffect(() => {
    fetchUserProfile(user.id);
  }, [user.id]);

  // Use userProfile data if available, otherwise fallback to user prop
  const displayData = userProfile || user;
  const displayName = displayData.name || extractUsername(displayData.email);
  const displayUsername = displayData.username
    ? extractUsername(displayData.username)
    : null;

  const ButtonAction = () => {
    switch (userProfile?.relationshipStatus) {
      case "ACCEPTED":
        return (
          <div className="flex justify-center space-x-2">
            <Button
              size="sm"
              onClick={() => onMessage?.(user.id)}
              className="bg-blue-600 hover:bg-blue-700 px-4"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Nhắn tin
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onCall?.(user.id)}
              className="border-gray-200 hover:bg-gray-50"
            >
              <Phone className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onVideoCall?.(user.id)}
              className="border-gray-200 hover:bg-gray-50"
            >
              <Video className="w-4 h-4" />
            </Button>
          </div>
        );
      case "REQUESTED":
        if (type === "received") {
          return (
            <div className="flex justify-center gap-2">
              <Button
                onClick={() => onAcceptRequest?.(user.id)}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Chấp nhận
              </Button>
              <Button
                onClick={() => onDeclineRequest?.(user.id)}
                variant="outline"
                className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
              >
                <X className="w-4 h-4 mr-2" />
                Từ chối
              </Button>
            </div>
          );
        } else {
          return (
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                onClick={() => onCancelRequest?.(user.id)}
              >
                <X className="w-4 h-4 mr-2" />
                Hủy lời mời
              </Button>
            </div>
          );
        }
      case "RECEIVED":
        return (
          <div className="flex justify-center space-x-2">
            <Button
              variant="outline"
              onClick={() => onCancelRequest?.(user.id)}
            >
              <X className="w-4 h-4 mr-2" />
              Hủy lời mời
            </Button>
          </div>
        );
      default:
        return (
          <div>
            <Button
              className="bg-blue-600 hover:bg-blue-700 px-4"
              onClick={() => onAddFriend?.(user.id)}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Kết bạn
            </Button>
          </div>
        );
    }
  };
  return (
    <div className="h-full bg-white  ">
      <div className="pb-6">
        <div className="text-center space-y-4">
          {/* Avatar */}
          <div className="relative mx-auto w-24 h-24">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-600 rounded-full p-1 shadow-lg">
              <Avatar className="w-full h-full border-2 border-white">
                <AvatarImage
                  src={displayData.photo || ""}
                  alt={displayName}
                  className="object-cover"
                />
                <AvatarFallback className="text-2xl font-bold bg-gradient-to-br from-blue-100 to-purple-100 text-gray-700">
                  {displayName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </div>
          </div>

          {/* Name và Status */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              {displayName}
            </h2>
            {displayUsername && (
              <p className="text-sm text-gray-500 mb-2">@{displayUsername}</p>
            )}
          </div>
          <ButtonAction />
        </div>
      </div>

      <div className="space-y-6">
        {/* Loading State */}
        {isLoadingProfile && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            <span className="text-sm text-gray-500">Đang tải thông tin...</span>
          </div>
        )}

        {/* Error State */}
        {profileError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-600">{profileError}</p>
          </div>
        )}

        {/* Thông tin cơ bản */}
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">
            Thông tin liên hệ
          </h3>

          <div className="space-y-3">
            <div className="flex items-center space-x-3 text-sm">
              <Mail className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">{displayData.email}</span>
            </div>

            {displayUsername && (
              <div className="flex items-center space-x-3 text-sm">
                <User className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">@{displayUsername}</span>
              </div>
            )}
          </div>
        </div>

        {/* Company */}
        {displayData.company && (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">
              Công ty
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              {displayData.company}
            </p>
          </div>
        )}

        {/* Bio */}
        {displayData.biography && (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">
              Giới thiệu
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              {displayData.biography}
            </p>
          </div>
        )}

        {/* Unfriend Button */}
        {userProfile?.relationshipStatus === "ACCEPTED" && (
          <div className="pt-4 border-t border-gray-100">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onUnfriend?.(user.id)}
              className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <UserMinus className="w-4 h-4 mr-2" />
              Hủy kết bạn
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfileCard;
